/**
 * @file AgentProfile.jsx
 * @description AgentProfile component for displaying and managing agent profile.
 * @component AgentProfile
 * @exports AgentProfile - Main component for displaying and managing agent profile.
 */

import React, { useState, useEffect } from "react";
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Box } from "@mui/material";
import { SIZES, VARIANTS, BUTTON_COLORS } from "../../../common/constants/commonConstants.js";
import { PROFILE_TABLE_COLUMNS, PROFILE_TABLE_COLUMN_KEYS, STATUS_STRINGS } from "../../../common/constants/agentModule/statusConstants.js";

const AgentProfile = ({ agentStatus, acsIdentity, expirationDate, handleRefreshToken, profile }) => {
    const workerId = localStorage.getItem("workerid");
    const userGUID = localStorage.getItem("userguid");
    const [isExpired, setIsExpired] = useState(false);
    const [tokenStatus, setTokenStatus] = useState("");
    const lastName = profile.lastname && profile.lastname.length <= 2 
        ? profile.lastname.toUpperCase() 
        : profile.lastname;
    const expirationStatus = expirationDate.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    const updateCountdown = () => {
        if (!expirationDate) return;
        const currentDate = new Date();
        const diffMs = expirationDate - currentDate;
        if (diffMs <= 0) {
            setTokenStatus(STATUS_STRINGS.EXPIRED);
            setIsExpired(true);
            return;
        }
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const diffSeconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        const hoursLabel = diffHours === 1 ? "Hr" : "Hrs";
        const minutesLabel = diffMinutes === 1 ? "Min" : "Mins";
        const secondsLabel = diffSeconds === 1 ? "Sec" : "Secs";
        setTokenStatus(
            `Expires in ${diffHours}${hoursLabel} ${diffMinutes}${minutesLabel} ${diffSeconds}${secondsLabel}`
        );
        setIsExpired(false);
    };
    useEffect(() => {
        updateCountdown();
        const timer = setInterval(updateCountdown, 1000);
        return () => clearInterval(timer);
    }, [expirationDate]);

    return (
        (agentStatus && profile) && (
            <>
                <TableContainer sx={{ mt: 2 }}>
                    <Table size={SIZES.SMALL}>
                        <TableHead>
                            <TableRow>
                                {PROFILE_TABLE_COLUMNS.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sx={{
                                            width:
                                                column.id === PROFILE_TABLE_COLUMN_KEYS.FIRSTNAME ? 150 :
                                                column.id === PROFILE_TABLE_COLUMN_KEYS.LASTNAME ? 150 :
                                                column.id === PROFILE_TABLE_COLUMN_KEYS.EMAIL ? 200 :
                                                column.id === PROFILE_TABLE_COLUMN_KEYS.ROLE ? 100 :
                                                column.id === PROFILE_TABLE_COLUMN_KEYS.EXTENSION ? 75 : 200
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {profile && (
                                <TableRow hover tabIndex={-1} key={profile.email}>
                                    <TableCell>
                                        {profile.firstname}
                                    </TableCell>
                                    <TableCell>
                                        {lastName}
                                    </TableCell>
                                    <TableCell>
                                        {profile.email}
                                    </TableCell>
                                    <TableCell>
                                        {profile.rolename}
                                    </TableCell>
                                    <TableCell>
                                        {profile.extension}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TableContainer sx={{ mt: 2 }}>
                    <Table size={SIZES.SMALL}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>
                                    {STATUS_STRINGS.WORKER_ID}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: '50%' }}>
                                    {STATUS_STRINGS.AGENT_GUID}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ width: '50%' }}>
                                    {workerId}
                                </TableCell>
                                <TableCell sx={{ width: '50%' }}>
                                    {userGUID}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <TableContainer sx={{ mt: 2 }}>
                    <Table size={SIZES.SMALL}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                    {STATUS_STRINGS.ACS_IDENTITY}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    {acsIdentity}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <TableContainer sx={{ mt: 2 }}>
                    <Table size={SIZES.SMALL}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: '70%' }}>
                                    {STATUS_STRINGS.ACS_IDENTITY_STATUS}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                        <div>
                                            <p style={{ margin: "0", fontWeight: "bold", color: isExpired ? "red" : "green" }}>{tokenStatus}</p>
                                            <p style={{ margin: "0" }}>{`${isExpired ? "On" : "At"} ${expirationStatus}`}</p>
                                        </div>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <Button variant={VARIANTS.CONTAINED} size={SIZES.SMALL} color={BUTTON_COLORS.PRIMARY} onClick={handleRefreshToken}>
                                                {STATUS_STRINGS.REFRESH}
                                            </Button>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        )
    );
};

export default AgentProfile;